import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type { BrandCrowdSignals, BrandReaction } from "@shared/types";
import { useAuth } from "../app/AuthContext";
import { api } from "../lib/api";

const REACTIONS: Array<{ id: BrandReaction; label: string; hint: string }> = [
  { id: "early", label: "Early", hint: "Found it before most people" },
  { id: "would_wear", label: "I'd wear", hint: "The product feels right" },
  { id: "mainstream", label: "Too mainstream", hint: "Already feels obvious" },
  { id: "not_it", label: "Not it", hint: "The signal is weak" },
];

const EMPTY: BrandCrowdSignals = {
  counts: { early: 0, would_wear: 0, mainstream: 0, not_it: 0 },
  myReactions: [],
};

const PENDING_REACTION_KEY = "bm.pendingReaction";

export function CrowdSignalBar({ brandId, initial }: { brandId: string; initial?: BrandCrowdSignals }) {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const [signals, setSignals] = useState<BrandCrowdSignals>(initial ?? EMPTY);
  const [pending, setPending] = useState<BrandReaction | null>(null);

  useEffect(() => {
    if (!user || pending) return;
    const raw = sessionStorage.getItem(PENDING_REACTION_KEY);
    if (!raw) return;

    let saved: { brandId?: string; reaction?: BrandReaction } | null = null;
    try {
      saved = JSON.parse(raw) as { brandId?: string; reaction?: BrandReaction };
    } catch {
      sessionStorage.removeItem(PENDING_REACTION_KEY);
      return;
    }

    if (saved?.brandId !== brandId || !isBrandReaction(saved.reaction)) return;
    sessionStorage.removeItem(PENDING_REACTION_KEY);

    setPending(saved.reaction);
    api.brands.react(brandId, saved.reaction, true)
      .then((res) => setSignals(res.crowdSignals))
      .finally(() => setPending(null));
  }, [brandId, pending, user]);

  const toggle = async (reaction: BrandReaction) => {
    if (pending) return;
    if (!user) {
      sessionStorage.setItem(PENDING_REACTION_KEY, JSON.stringify({ brandId, reaction }));
      signIn(`${location.pathname}${location.search}`);
      return;
    }
    const active = !signals.myReactions.includes(reaction);
    setPending(reaction);
    try {
      const res = await api.brands.react(brandId, reaction, active);
      setSignals(res.crowdSignals);
    } finally {
      setPending(null);
    }
  };

  const totalVotes = REACTIONS.reduce((sum, reaction) => sum + (signals.counts[reaction.id] ?? 0), 0);
  const positiveVotes = signals.counts.early + signals.counts.would_wear;
  const negativeVotes = signals.counts.mainstream + signals.counts.not_it;
  const crowdVerdict =
    totalVotes === 0 ? "Be first to set the signal"
    : positiveVotes > negativeVotes ? "Crowd says this is early"
    : negativeVotes > positiveVotes ? "Crowd is cooling on this"
    : "Crowd is split";

  return (
    <section className="crowd-signals" aria-label="Crowd signals">
      <div className="crowd-signals__head">
        <div>
          <h2>The crowd decides</h2>
          <p>{crowdVerdict}. Your vote changes how this brand ranks on the radar.</p>
        </div>
        <span>{totalVotes} {totalVotes === 1 ? "vote" : "votes"}</span>
      </div>
      <div className="crowd-signals__actions">
        {REACTIONS.map((reaction) => {
          const active = signals.myReactions.includes(reaction.id);
          return (
            <button
              key={reaction.id}
              type="button"
              className={active ? "crowd-signal is-active" : "crowd-signal"}
              disabled={pending !== null}
              onClick={() => toggle(reaction.id)}
            >
              <span className="crowd-signal__label">
                <span>{reaction.label}</span>
                <small>{active ? "Your vote" : reaction.hint}</small>
              </span>
              <b aria-label={`${signals.counts[reaction.id] ?? 0} votes`}>{signals.counts[reaction.id] ?? 0}</b>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function isBrandReaction(value: unknown): value is BrandReaction {
  return typeof value === "string" && REACTIONS.some((reaction) => reaction.id === value);
}
